import React, { useEffect, useState } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import {
  fetchAvailabilityRules,
  createAvailabilityRule,
  updateAvailabilityRule,
  updateAvailabilityRuleStatus,
  deleteAvailabilityRule,
} from '../services/availabilityRuleService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import {
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Trash2,
} from 'lucide-react';

export default function AvailabilityRules() {
  const [rules, setRules] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({
    name: '',
    days: '',
    startTime: '',
    endTime: '',
    isActive: '1',
  });
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('DESC');

  const loadRules = async () => {
    try {
      const res = await fetchAvailabilityRules({ search, sortBy, sortOrder });
      const records = res.data?.data?.records || res.data?.data || [];
      setRules(records);
    } catch (err) {
      toast.error('Failed to load rules');
    }
  };

  useEffect(() => {
    loadRules();
  }, [sortBy, sortOrder]);

  useEffect(() => {
    if (search === '') loadRules();
  }, [search]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      days: form.days
        .split(',')
        .map((d) => d.trim())
        .filter(Boolean),
      isActive: Number(form.isActive),
    };
    try {
      if (editingId) {
        await updateAvailabilityRule(editingId, payload);
        toast.success('Rule updated');
      } else {
        await createAvailabilityRule(payload);
        toast.success('Rule created');
      }
      closeForm();
      loadRules();
    } catch (err) {
      toast.error('Failed to save rule');
    }
  };

  const openEdit = (rule) => {
    setEditingId(rule.id);
    setForm({
      name: rule.name,
      days: Array.isArray(rule.days) ? rule.days.join(',') : '',
      startTime: rule.startTime,
      endTime: rule.endTime,
      isActive: String(rule.isActive ? 1 : 0),
    });
    setOpen(true);
  };

  const closeForm = () => {
    setEditingId(null);
    setForm({ name: '', days: '', startTime: '', endTime: '', isActive: '1' });
    setOpen(false);
  };

  const toggleStatus = async (rule) => {
    const newStatus = rule.isActive ? 0 : 1;
    const action = newStatus === 1 ? 'activate' : 'deactivate';
    const result = await Swal.fire({
      title: `Are you sure you want to ${action} this rule?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-green-600 text-white px-4 py-2 rounded mr-2 hover:bg-green-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await updateAvailabilityRuleStatus(rule.id, newStatus);
      toast.success('Status updated');
      loadRules();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (rule) => {
    const result = await Swal.fire({
      title: 'Are you sure you want to delete this rule?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      buttonsStyling: false,
      customClass: {
        confirmButton:
          'bg-red-600 text-white px-4 py-2 rounded mr-2 hover:bg-red-700',
        cancelButton:
          'bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600',
      },
    });
    if (!result.isConfirmed) return;
    try {
      await deleteAvailabilityRule(rule.id);
      toast.success('Rule deleted');
      loadRules();
    } catch (err) {
      toast.error('Failed to delete rule');
    }
  };

  return (
    <LayoutWithSidebar>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 space-y-2 md:space-y-0">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            loadRules();
          }}
          className="relative"
        >
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="border px-3 py-2 pl-8 pr-8 rounded w-full md:w-64 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                loadRules();
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            >
              <X size={16} />
            </button>
          )}
        </form>

        <button
          onClick={() => setOpen(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded"
        >
          Add Rule
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Name</span>
                  {sortBy === 'name' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Days</th>
              <th className="py-2 px-3">Time</th>
              <th
                className="py-2 px-3 cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center space-x-1">
                  <span>Created At</span>
                  {sortBy === 'createdAt' ? (
                    sortOrder === 'ASC' ? (
                      <ArrowUp size={12} />
                    ) : (
                      <ArrowDown size={12} />
                    )
                  ) : (
                    <ArrowUpDown size={12} />
                  )}
                </div>
              </th>
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id} className="border-t dark:border-gray-700">
                <td className="py-2 px-3">{rule.name}</td>
                <td className="py-2 px-3">
                  {Array.isArray(rule.days) ? rule.days.join(', ') : ''}
                </td>
                <td className="py-2 px-3">
                  {rule.startTime} - {rule.endTime}
                </td>
                <td className="py-2 px-3">
                  {new Date(rule.createdAt).toLocaleDateString()}
                </td>
                <td className="py-2 px-3">
                  <button
                    className={`px-2 py-1 rounded text-white ${rule.isActive ? 'bg-green-500' : 'bg-red-500'}`}
                    onClick={() => toggleStatus(rule)}
                    title={`${rule.isActive ? 'Deactivate' : 'Activate'} rule`}
                  >
                    {rule.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="py-2 px-3 space-x-2">
                  <button
                    className="text-indigo-600"
                    onClick={() => openEdit(rule)}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    className="text-red-600"
                    onClick={() => handleDelete(rule)}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {rules.length === 0 && (
              <tr>
                <td className="py-2 px-3 text-center" colSpan="6">
                  <p className="text-sm text-gray-500 dark:text-gray-300">
                    No rules
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-25 dark:bg-opacity-50 flex justify-end">
          <div className="relative bg-white dark:bg-gray-800 dark:text-white w-full sm:w-96 h-full p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">{editingId ? 'Edit Rule' : 'Add Rule'}</h2>
              <button onClick={closeForm} className="text-gray-600 dark:text-gray-300">
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <input
                name="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Name"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                name="days"
                value={form.days}
                onChange={(e) => setForm({ ...form, days: e.target.value })}
                placeholder="Days (e.g. mon,tue)"
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                type="time"
                name="startTime"
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <input
                type="time"
                name="endTime"
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <select
                name="isActive"
                value={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.value })}
                className="border px-3 py-2 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-4 py-2 rounded"
              >
                {editingId ? 'Update' : 'Create'} Rule
              </button>
            </form>
          </div>
        </div>
      )}
    </LayoutWithSidebar>
  );
}
